import React from 'react';
import { useLingui } from '@lingui/react/macro';

import Close from '@rover/icons/svg/close.svg';
import { Box, Button, Flex, Paragraph } from '@rover/kibble/core';
import type { ThemeParagraphSizes } from '@rover/kibble/styles/themeTypes';
import useTheme from '@rover/kibble/themes/useTheme';
import type { SvgIcon } from '@rover/types/src/SvgIcon';

import { BannerSeverity } from './BannerSeverity.constants';
import {
  getBackgroundColorForSeverity,
  getIconFillColorForSeverity,
  getIconForSeverity,
} from './BannerSeverity.utils';

/**
 * Props for Banner, a hybrid component rendering on both web and React Native.
 *
 * There is intentionally no `.native.tsx`: Banner composes only cross-platform core
 * primitives (`Box`, `Flex`, `Paragraph`, `Button`) and handles the platform split through
 * `webProps`/`rxnProps`, so this one file serves both targets.
 *
 * To attach a call to action, use `FooterComponent` rather than nesting an interactive
 * element in `children`: on React Native `children` becomes a single Text, so an element
 * nested in there cannot take a line of its own.
 */
export type Props = {
  title?: React.ReactNode;
  children?: React.ReactNode;
  severity?: `${BannerSeverity}`;
  testID?: string;
  containerProps?: React.ComponentProps<typeof Box>;
  icon?: SvgIcon;
  iconFillColor?: string;
  hideIcon?: boolean;
  onClose?: () => void;
  fontSize?: ThemeParagraphSizes | ThemeParagraphSizes[];
  FooterComponent?: React.ReactNode;
  /**
   * Renders `children` as given instead of wrapping them in the body `Paragraph`. Use it for
   * block-level content, such as a list, which is invalid inside a paragraph. The caller then
   * owns the body typography and must supply its own `Paragraph` or `Text` around any copy.
   */
  suppressDefaultTypography?: boolean;
  /**
   * Makes the whole banner a tap target. This is an edge case — reach for it only when the
   * banner itself is the affordance, such as a dismissible informational bar that opens the
   * thing it describes.
   *
   * Do not use it for a call to action or a link: those belong in `FooterComponent`, which
   * gives them real button or link semantics. The container here is a status region, so a
   * press handler on it is not keyboard reachable and has no accessible role of its own —
   * supply an `aria-label`/`accessibilityRole` through `containerProps` when you use this.
   */
  onPress?: React.ComponentProps<typeof Box>['onPress'];
  webProps?: BannerWebOnlyProps;
  rxnProps?: BannerRxnOnlyProps;
};

/** Reached so a caller managing several banners can move focus to the ✕ when one is removed. */
export type BannerWebOnlyProps = {
  closeButtonRef?: React.ForwardedRef<HTMLElement>;
};

export type BannerRxnOnlyProps = {
  closeButtonRef?: React.ForwardedRef<React.ElementType>;
};

const defaultProps = {
  severity: BannerSeverity.INFO,
};
const Banner = ({
  title,
  children,
  severity = defaultProps.severity,
  testID = 'banner-block',
  containerProps = {},
  icon = undefined,
  iconFillColor = undefined,
  hideIcon = false,
  onClose = undefined,
  fontSize = '100',
  FooterComponent = undefined,
  suppressDefaultTypography = false,
  onPress = undefined,
  webProps = {},
  rxnProps = {},
}: Props): JSX.Element | null => {
  const { t } = useLingui();
  const theme = useTheme();
  const backgroundColor = getBackgroundColorForSeverity(severity as BannerSeverity, theme);
  const Icon = icon || getIconForSeverity(severity as BannerSeverity);
  if (!title && !children && !FooterComponent) return null;
  return (
    <Box
      flexDirection="row"
      justifyContent="space-between"
      p="4x"
      alignItems="flex-start"
      aria-live="polite"
      webProps={{
        role: 'status',
      }}
      rxnProps={{
        accessibilityRole: 'alert',
      }}
      testID={testID}
      borderRadius="secondary"
      onPress={onPress}
      {...containerProps}
      sx={{
        backgroundColor,
        ...containerProps?.sx,
      }}
    >
      <Flex flex="1" flexDirection="row">
        {!hideIcon && Icon && (
          <Box pr="3x" height={24}>
            <Icon
              width={24}
              height={24}
              {...(process.env.JS_ENV_NATIVE
                ? { testID: 'banner-block-icon' }
                : { 'data-testid': 'banner-block-icon' })}
              fill={iconFillColor || getIconFillColorForSeverity(severity as BannerSeverity, theme)}
            />
          </Box>
        )}
        <Box flex="1">
          {title && (
            <Flex justifyContent="space-between">
              <Paragraph fontWeight="semibold" size={fontSize} marginBottom="1x">
                {title}
              </Paragraph>
            </Flex>
          )}
          {children &&
            (suppressDefaultTypography ? (
              children
            ) : (
              <Paragraph mb="0x" size={fontSize}>
                {children}
              </Paragraph>
            ))}
          {FooterComponent && (
            <Flex mt="2x" alignItems="flex-start">
              {FooterComponent}
            </Flex>
          )}
        </Box>
        {onClose && (
          <Box height={24} pl="1x">
            <Button
              onPress={onClose}
              icon={Close}
              size="small"
              aria-label={t`Close`}
              variant="flat"
              sx={{ color: theme.textColor.tertiary.toString(), padding: 0 }}
              webProps={{ ref: webProps.closeButtonRef }}
              rxnProps={{ ref: rxnProps.closeButtonRef }}
            />
          </Box>
        )}
      </Flex>
    </Box>
  );
};
export default Banner;
